import React, { FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useShallow } from 'zustand/react/shallow'
import Agent from "../../models/Agent";
import Dependent from "../../models/Dependent";
import Member, { ICreateMemberResponse } from "../../models/Member";
import { IValidateTransactionResponse, validateTransaction } from "../../services/validateTransaction";
import Validation from "../Validation"; // Import the PaymentModal component
import Payment from "../../models/Payment";
import Plan from "../../models/Plan";
import AgentLicense from "../../models/AgentLicense";
import { D2CAgentId, formatDate, isBillingCurrent, buildNCDHeaders, svgBillingDateCheck, SVGUplines } from "../../utils";
import useApplicationStore from "../../context/application";
import BillingDateModal from "../BillingDateModal";
import ErrorModal from "../../components/Modals/ErrorModal";
import AgreementForm from "./AgreementForm";
import axios from "axios";
import Submitted from "./Submitted";
import ErrorLog, { LogLevel } from "../../models/ErrorLog";
import { recordRumEvent, recordRumPageView, RUM_EVENTS } from "../../services/awsRum";
import hotJar from "../../services/hotJar";

type AgreementsProps = {
  agent: Agent;
  appId?: string;
  member: Member;
  payment: Payment;
  selectedPlans: Plan[];
  associatedPlan: Plan;
  zipCodeState: string;
  dependents: Dependent[];
  availableStatesForAgent: AgentLicense[];
  billingErrorShown: boolean;
  step: number;
  stepsNames: string[];
  wantsDental: boolean;
  wantsVision: boolean;
  validDental: boolean;
  validVision: boolean;
  dentalPlans: Plan[] | undefined;
  visionPlans: Plan[] | undefined;
  dentalAssociationPlans: Plan[] | undefined;
  visionAssociationPlans: Plan[] | undefined;
  dentalVisionAssociationPlans: Plan[] | undefined;
  amountOfDependents: number;
  amountOfDependentsType: string;
  planDental: Plan;
  planVision: Plan;
  setSaveForLater: React.Dispatch<React.SetStateAction<boolean>>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  setMember: React.Dispatch<React.SetStateAction<Member>>;
  setBillingErrorShown: React.Dispatch<React.SetStateAction<boolean>>;
  setPurchaseState: React.Dispatch<React.SetStateAction<boolean>>;
};

const Agreements: FC<AgreementsProps> = ({
  agent,
  member,
  appId,
  selectedPlans,
  associatedPlan,
  dependents,
  zipCodeState,
  availableStatesForAgent,
  payment,
  billingErrorShown,
  step,
  stepsNames,
  wantsDental,
  wantsVision,
  validDental,
  validVision,
  dentalPlans,
  visionPlans,
  dentalAssociationPlans,
  visionAssociationPlans,
  dentalVisionAssociationPlans,
  amountOfDependents,
  amountOfDependentsType,
  planDental,
  planVision,
  setSaveForLater,
  setStep,
  setMember,
  setBillingErrorShown,
  setPurchaseState,
}: AgreementsProps) => {
  const [agreement, setAgreement] = useState(false);
  const [agreementTwo, setAgreementTwo] = useState(Number(agent?.id) !== D2CAgentId);
  const [didNotAgreeToTheTerms, setDidNotAgreeToTheTerms] = useState(false);
  const [invalidBillingDate, setInvalidBillingDate] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [paymentErrorCode, setPaymentErrorCode] = useState<string | string[] | qs.ParsedQs | qs.ParsedQs[] | undefined>(undefined);

  const [invalidAnimation, setInvalidAnimation] = useState(false);
  const [submitState, setSubmitState] = useState({
    loading: false,
  });
  const [validationAttempt, setValidationAttempt] = useState(0);

  const setApplicationDate = useApplicationStore(useShallow((state) => state.setApplicationDate));

  const agentId: number = agent.id!;

  function getFirstDayOfMonth(year: number, month: number) {
    return new Date(year, month, 1);
  }

  const dismissInvalidBillingDate = useCallback(() => {
    setInvalidBillingDate(false);
  }, [])

  useEffect(() => {
    setAgreementTwo(Number(agent?.id) !== D2CAgentId);
  }, [agent]);

  useEffect(() => {
    // TODO this doesn't appear to do anything...remove?
    let value = 0;
    [...selectedPlans, associatedPlan].forEach((plan) => {
      if (plan?.rate) {
        value = value + plan?.rate;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlans]);

  // TODO there is a chance the billing date could be incremented and be farther in the future than effectiveDate
  // ex: billing date is 4/30/2025...but SVG rules determine billingDate should be incremented 3 days in the future
  // ...and if original effectiveDate was 5/1 then we will have an issue.
  const updateBillingandPaymentProcess = useCallback((newBillingDate: string) => {
    const isToday = newBillingDate === formatDate(new Date());

    const updatedValues = {
      billingDate: newBillingDate,
      paymentProcess: isToday ? "Y" : "N",
    }
    setMember((prev) => ({...prev,...updatedValues}));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const handleIncorrectBillingDate = useCallback(() => {
    // Update billing date
    const newBillingDate = new Date();
    const newEffectiveDate = new Date(newBillingDate.getFullYear(), newBillingDate.getMonth() + 1, 1);
    updateBillingandPaymentProcess(formatDate(newBillingDate))

    const nextMonth = getFirstDayOfMonth(newBillingDate.getFullYear(), newBillingDate.getMonth() + 1)

    // Validate effectiveDate; if invalid, set to month after billingDate
    if (!member.effectiveDate || (member.effectiveDate && (new Date(member.effectiveDate) <= nextMonth))) {
      member.effectiveDate = formatDate(newEffectiveDate);
      setMember((prev) => ({ ...prev, effectiveDate: member.effectiveDate}))
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.effectiveDate, setMember, updateBillingandPaymentProcess]);

  useEffect(() => {
    if (invalidBillingDate) handleIncorrectBillingDate()
  }, [invalidBillingDate, handleIncorrectBillingDate])

  // Custom to this agent to only allow one billing operation on a day for a unique last4 and zip
  // Potentially add this to FeatureFlags as a feature like "billingDateCheck"
  const svgHandleBillingUpdate = useCallback(async() => {
    if (SVGUplines.includes(agent.parentAgentId!)) {
      try {
        let last4Digits = payment.paymentType === "CC" ? payment.ccNumber?.slice(-4) : payment.achAccount?.slice(-4);
        let currentBillingDate = member.billingDate;
        const response = await axios.post(`${svgBillingDateCheck}`, {
          last4Digits: last4Digits,
          zip: member.zipCode,
          dtBilling: currentBillingDate,
        }, {
          headers: buildNCDHeaders(agentId, appId)
        });
        const { _success, _message, billingDate: newBillingDate } = response.data;

        // Update Billing Date if successfully incremented (limit 5) or no new billing date needed.
        // also Update Billing Date if max limit was reached. Essentially, always update.
        updateBillingandPaymentProcess(newBillingDate);
        return true
      } catch (error) {
        // console.error('Failed to send billing date update request:', error);
        // setInvalidAnimation(true);
        return true
      }
    }
  },[agent.parentAgentId, agentId, appId, member.billingDate, member.zipCode, payment.achAccount, payment.ccNumber, payment.paymentType, updateBillingandPaymentProcess])

  const navigate = useNavigate();

  const createMemberFailed = useCallback((response: ICreateMemberResponse) => {
    const msg = `Error creating member: ${response}`
    ErrorLog.log(new Error(msg), msg, LogLevel.warn);

    return false;
  },[])

  const createMemberSuccess = useCallback(() => {
    setApplicationDate(new Date().getMonth());
    navigate(`/completed/${agentId}`)

    return true;
  },[agentId, navigate, setApplicationDate])

  const updatePaymentInfo = useCallback((newPayment: Payment) => {
    member.payment = newPayment; // this will get carried over to member.createMember
    setMember((prev) => ({
      ...prev,
      payment: newPayment,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const onValidationResponseHardError = useCallback((validationResponse: IValidateTransactionResponse, stepName: string) => {
    setPaymentModalVisible(true);
    setPaymentErrorCode(validationResponse.responseCode);
    const eventData = { agentId, appId, paymentType: payment.paymentType, errorCode: validationResponse.responseCode, attempt: validationResponse.validationAttempt }
    recordRumEvent(RUM_EVENTS.PAYMENT_VALIDATION_ERROR, eventData)
    hotJar.trackEvent(RUM_EVENTS.PAYMENT_VALIDATION_ERROR)
    recordRumPageView(`${stepName}-ccValidationAttempt${validationResponse.validationAttempt}`)
    hotJar.recordPageView(`${stepName}-ccValidationAttempt${validationResponse.validationAttempt}`)
    setSubmitState({ loading: false });
    setPurchaseState(false);
  },[agentId, appId, payment.paymentType, setPurchaseState])

  const handlePurchaseButton = useCallback(async () => {
    if (!isBillingCurrent(member.billingDate) && !invalidBillingDate) {
      setInvalidBillingDate(true);
      return;
    }

    if (!agreement || !agreementTwo) {
      setDidNotAgreeToTheTerms(true);
      return;
    } else {
      setDidNotAgreeToTheTerms(false);
    }

    // manually set member instance values that have historically been set within this component, but should probably be set when these values are generated...
    member.agent = member.agent ?? agent
    member.dependents = dependents
    member.payment = member.payment ?? payment // sets member.payment to original payment form's payment, only in the case that member.payment doesn't exist
    member.paymentProcess = member.billingDate === formatDate(new Date()) ? "Y" : "N"

    const stepName = stepsNames[step - 1];

    setSubmitState({ loading: true });
    setPurchaseState(true);
    const validationResponse: IValidateTransactionResponse | undefined = await validateTransaction(
      agentId,
      appId as string,
      member.payment,
      setValidationAttempt,
      validationAttempt
    );

    if (validationResponse?.showModal) {
      onValidationResponseHardError(validationResponse, stepName)
      return;
    }

    setView('Submitted'); // we will change to Completed once response returns
    recordRumPageView('Submitted')
    hotJar.recordPageView('Submitted')

    await svgHandleBillingUpdate();

    const eventData = {stepName: stepName, agentId, parentAgentId: agent?.parentAgentId, appId, selectedPlans, zipCodeState, paymentType: payment.paymentType, billingDate: member.billingDate}
    recordRumEvent(RUM_EVENTS.APP_SUBMIT, eventData)
    if (validationResponse && validationResponse.responseCode && validationResponse.validationAttempt > 0) {
      recordRumEvent(RUM_EVENTS.PAYMENT_VALIDATION_CONVERSION, {...eventData, paymentErrorCode, attempt: validationResponse.validationAttempt})
      hotJar.trackEvent(RUM_EVENTS.PAYMENT_VALIDATION_CONVERSION)
    }

    // creating a new Member instance to ensure our Member instance we will use to create the member is synced with state:
    // if we skip this, member instance `this` values could be stale:
    const memberToCreate = Member.newMemberFromObject(member)
    
    memberToCreate.createMember(
        selectedPlans,
        associatedPlan,
        agentId!,
        zipCodeState,
        availableStatesForAgent,
        wantsDental,
        wantsVision,
        setMember,
        appId
      )
      .then((response: ICreateMemberResponse) => {
        const { state } = response ?? {};

        return state
          ? createMemberSuccess()
          : createMemberFailed(response);
      });
  }, [
    agent.parentAgentId,
    agentId,
    agreement,
    agreementTwo,
    appId,
    associatedPlan,
    availableStatesForAgent,
    createMemberFailed,
    createMemberSuccess,
    member,
    onValidationResponseHardError,
    payment,
    paymentErrorCode,
    selectedPlans,
    step,
    stepsNames,
    svgHandleBillingUpdate,
    validationAttempt,
    wantsDental,
    wantsVision,
    zipCodeState,
  ]);

  const [view, setView] = useState('AgreementForm');

  return (
    <div className="xl:pt-17 gap flex min-h-[70vh] w-full flex-col gap-1 px-2 py-2">
      {view === 'AgreementForm' &&
        <AgreementForm
          agentId={agent?.id}
          agreement={agreement}
          agreementTwo={agreementTwo}
          handlePurchaseButton={handlePurchaseButton}
          didNotAgreeToTheTerms={didNotAgreeToTheTerms}
          setAgreement={setAgreement}
          setAgreementTwo={setAgreementTwo}
          submitState={submitState}
        />
      }
      {view === 'Submitted' &&
        <Submitted agentId={agentId} memberId={undefined} memberFirstName={member.firstName} memberLastName={member.lastName} setMember={setMember} />
      }
      <ErrorModal visible={invalidAnimation} message="There was an error while completing the purchase, please check your data and try again later" />
      {invalidBillingDate && <BillingDateModal dismissInvalidBillingDate={dismissInvalidBillingDate} newBillingDate={member.billingDate!} newEffectiveDate={member.effectiveDate!} />}
      <Validation
        agentId={agentId}
        visible={paymentModalVisible}
        onClose={() => setPaymentModalVisible(false)}
        updatePaymentInfo={updatePaymentInfo}
        errorCode={paymentErrorCode}
      />
    </div>
  );
};

export default Agreements;